import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import type { LucideIcon } from 'lucide-react';
import { ItemIcon } from '@/components/item-icon';
import { ArtifactView, WhenVisible } from '@/features/apps/artifact-view';
import { renderLineLabel } from '@/features/apps/renders';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { PluginWorkbench } from '@/features/plugins/plugin-workbench';
import { categoryOf, coreGlyph, kindOf, pluginKindOf, shortAgo } from './library-model';

/** What shows an artifact when nothing else does: its app's icon, or the core type's glyph. */
export type ArtifactGlyph = { icon: LucideIcon; src?: string | undefined };

/** Files the core's viewer has no picture of: a thumbnail would only show their path. */
const NO_PICTURE = /\.(docx|xlsx|pptx|odt|ods|odp|csv|mp3|wav|ogg|m4a|flac)$/i;

export function artifactGlyph(
  artifact: Pick<ArtifactListingEntry, 'plugin' | 'type'>,
  workbench: Pick<PluginWorkbench, 'panels'>,
): ArtifactGlyph {
  const panel = workbench.panels.find(
    (candidate) => candidate.plugin === artifact.plugin && candidate.rail !== undefined,
  );
  if (panel !== undefined) return { icon: pluginIcon(panel.icon ?? 'app'), src: panel.iconSrc };
  return { icon: coreGlyph(artifact.type) };
}

/**
 * Whether an artifact has a picture to preview: its app is enabled (its view draws it), or the
 * core's viewer draws its file. Otherwise its thumbnail is its glyph.
 */
export function hasPreview(
  artifact: Pick<ArtifactListingEntry, 'plugin' | 'view' | 'entry'>,
  workbench: Pick<PluginWorkbench, 'enabledPlugins'>,
): boolean {
  if (artifact.view !== null) return workbench.enabledPlugins.includes(artifact.plugin);
  return !NO_PICTURE.test(artifact.entry);
}

/**
 * An artifact's thumbnail, never empty: its glyph underneath, its live preview over it once on
 * screen (a preview still loading, or one that failed, leaves the glyph showing).
 */
function LibraryThumbnail({
  artifact,
  glyph,
  preview,
  className,
}: {
  artifact: ArtifactListingEntry;
  glyph: ArtifactGlyph;
  preview: boolean;
  className?: string;
}) {
  return (
    <div className={cn('relative overflow-hidden rounded-xl bg-control', className)}>
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center text-subtle-foreground"
        data-library-glyph
      >
        <ItemIcon className="size-8" icon={glyph.icon} src={glyph.src} />
      </span>
      {preview && (
        <WhenVisible className="pointer-events-none absolute inset-0">
          <ArtifactView artifact={artifact} container="preview" />
        </WhenVisible>
      )}
    </div>
  );
}

/**
 * One artifact of the Library: its thumbnail, its title, its kind and when it last changed. What
 * a plugin provides says what it is ("Template", "Design system"), never when, and has "Use".
 */
export function LibraryCard({
  artifact,
  glyph,
  preview,
  place = null,
  onOpen,
  onUse,
  using = false,
  provided = onUse !== undefined,
  origin = null,
  renders = [],
}: {
  artifact: ArtifactListingEntry;
  glyph: ArtifactGlyph;
  preview: boolean;
  /** Where it was made (its conversation's project), said instead of its kind when known. */
  place?: string | null;
  onOpen(artifact: ArtifactListingEntry): void;
  /** Starts a new conversation from it (a template, a design system). */
  onUse?: (artifact: ArtifactListingEntry) => void;
  using?: boolean;
  /** What a plugin provides: it says what it is, never when it changed. */
  provided?: boolean;
  /** The assistant that made it (an app's home): "From <assistant>", in its colour. */
  origin?: { name: string; color?: string | null } | null;
  /** Its renders (an MP4 of a video project), listed under it; each opens in its viewer. */
  renders?: readonly ArtifactListingEntry[];
}) {
  const kind = provided ? pluginKindOf(artifact) : (place ?? kindOf(artifact));
  const ago = provided ? '' : shortAgo(artifact.modifiedAt);
  return (
    <li className="relative">
      <button
        aria-label={artifact.title}
        className="group flex w-full flex-col gap-2.5 rounded-2xl bg-card p-2 text-left transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:bg-transparent max-sm:p-0 max-sm:hover:bg-transparent"
        data-library-card={categoryOf(artifact).key}
        // Picking it starts from it (an app's template): once.
        disabled={using && onUse === undefined}
        onClick={() => onOpen(artifact)}
        type="button"
      >
        <LibraryThumbnail
          artifact={artifact}
          className="aspect-[16/7] max-sm:aspect-[16/10] max-sm:rounded-2xl"
          glyph={glyph}
          preview={preview}
        />
        <span
          className={cn(
            'flex min-w-0 items-center gap-2.5 px-1.5 pb-1 max-sm:px-0',
            onUse !== undefined && 'pr-14 max-sm:pr-12',
          )}
        >
          <span
            aria-hidden
            className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-control text-muted-foreground max-sm:hidden"
          >
            <ItemIcon className="size-3.5" icon={glyph.icon} src={glyph.src} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-sm">{artifact.title}</span>
            <span className="type-caption block truncate text-muted-foreground">
              {origin === null ? (
                ago === '' ? (
                  kind
                ) : (
                  `${kind} · ${ago}`
                )
              ) : (
                <span className="inline-flex max-w-full items-center gap-1.5" data-card-origin>
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-[3px] bg-muted-foreground"
                    style={origin.color ? { backgroundColor: origin.color } : undefined}
                  />
                  <span className="truncate">
                    From {origin.name}
                    {ago === '' ? '' : ` · ${ago}`}
                  </span>
                </span>
              )}
            </span>
          </span>
        </span>
      </button>
      {onUse !== undefined && (
        <Button
          aria-label={`Use ${artifact.title}`}
          className="absolute right-3.5 bottom-3 z-10 max-sm:right-0 max-sm:bottom-0"
          disabled={using}
          onClick={() => onUse(artifact)}
          size="xs"
          type="button"
          variant="outline"
        >
          Use
        </Button>
      )}
      {renders.length > 0 && (
        <ul
          aria-label={`Renders of ${artifact.title}`}
          className="mt-1 flex flex-col gap-0.5 pl-4"
          data-library-renders
        >
          {renders.map((render) => (
            <li key={render.id}>
              <button
                className={cn(
                  'type-caption w-full truncate rounded-control px-2 py-1 text-left text-muted-foreground hover:bg-control hover:text-foreground',
                  render.derivedFrom?.stale && 'opacity-70',
                )}
                onClick={() => onOpen(render)}
                type="button"
              >
                {renderLineLabel(render)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
